import React, { useState, useEffect, useRef } from "react";
import { OliveLogo } from "./OliveLogo";
import { Menu, X, ShoppingCart, Instagram } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useCart } from "../lib/cart";
import { BUSINESS_INFO } from "../lib/business";
import LanguageSwitcher from "./LanguageSwitcher";

export const Navbar = ({ onShop, onOrder }) => {
  const { t } = useLang();
  const { totalCount, openCart, pulseToken } = useCart();
  const [open, setOpen] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const initial = useRef(true);

  // Trigger pulse when pulseToken changes (after addToCart)
  useEffect(() => {
    if (initial.current) { initial.current = false; return; }
    setPulsing(true);
    const t = setTimeout(() => setPulsing(false), 900);
    return () => clearTimeout(t);
  }, [pulseToken]);

  const links = [
    { label: t("nav.collections"), href: "#heritage", id: "products" },
    { label: t("nav.gallery"), href: "#gallery", id: "gallery" },
    { label: t("nav.story"), href: "#about", id: "about" },
    { label: t("nav.contact"), href: "#contact", id: "contact" },
  ];

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50"
      style={{
        background:
          "linear-gradient(to bottom, rgba(10,10,10,0.85) 0%, rgba(10,10,10,0.55) 70%, rgba(10,10,10,0) 100%)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        borderBottom: "1px solid rgba(201,169,97,0.18)",
        boxShadow: "0 8px 30px -12px rgba(0,0,0,0.65)",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center" data-testid="navbar-brand">
          <OliveLogo size={64} />
        </a>

        <nav className="hidden md:flex items-center gap-3">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-${l.id}`}
              className="btn-ghost-gold !py-2.5 !px-5 text-[10px]"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher className="hidden sm:block" />

          {/* Instagram icon */}
          <a
            href={BUSINESS_INFO.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-instagram"
            aria-label="Instagram @goldenelia_2026"
            className="hidden sm:inline-flex items-center justify-center w-[38px] h-[38px] border border-[color:var(--goldenelia-gold)]/45 hover:border-[color:var(--goldenelia-gold)] text-[color:var(--goldenelia-gold)] hover:text-[color:var(--goldenelia-cream)] bg-black/45 hover:bg-black/65 transition-all duration-200"
          >
            <Instagram size={16} strokeWidth={1.6} />
          </a>

          {/* Cart pill — clearer "ΚΑΛΑΘΙ" affordance */}
          <button
            data-testid="navbar-cart"
            onClick={openCart}
            className={`relative inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-[color:var(--goldenelia-gold)]/55 hover:border-[color:var(--goldenelia-gold)] text-[color:var(--goldenelia-gold)] hover:text-[color:var(--goldenelia-cream)] bg-black/45 hover:bg-black/65 transition-all duration-200 ${
              pulsing ? "animate-[cartpulse_900ms_ease-out]" : ""
            }`}
            aria-label={t("cart.title")}
          >
            <ShoppingCart size={17} strokeWidth={1.6} />
            <span className="font-display text-[10px] tracking-[0.3em] uppercase hidden sm:inline">
              {t("cart.title")}
            </span>
            {totalCount > 0 && (
              <span
                data-testid="navbar-cart-badge"
                className="ml-1 min-w-[20px] h-[20px] px-1.5 rounded-full bg-[color:var(--goldenelia-gold)] text-black text-[11px] font-bold flex items-center justify-center leading-none"
              >
                {totalCount}
              </span>
            )}
          </button>

          <button
            data-testid="navbar-cta"
            onClick={onOrder || onShop}
            className="hidden md:inline-flex btn-solid-gold !py-2.5 !px-5 text-[10px]"
          >
            {t("nav.order")}
          </button>

          <button
            data-testid="navbar-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-[color:var(--goldenelia-gold)]"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="md:hidden border-t border-[color:var(--goldenelia-gold)]/20"
          style={{
            background: "rgba(10,10,10,0.92)",
            backdropFilter: "blur(18px) saturate(140%)",
            WebkitBackdropFilter: "blur(18px) saturate(140%)",
          }}
        >
          <div className="px-6 py-5 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="btn-ghost-gold !py-2.5 self-start text-[10px]"
              >
                {l.label}
              </a>
            ))}
            <LanguageSwitcher />
            <a
              href={BUSINESS_INFO.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="navbar-mobile-instagram"
              className="btn-ghost-gold !py-2.5 self-start text-[10px] inline-flex items-center gap-2"
            >
              <Instagram size={14} /> Instagram
            </a>
            <button
              onClick={() => {
                setOpen(false);
                (onOrder || onShop)();
              }}
              className="btn-solid-gold !py-2.5 self-start text-[10px]"
            >
              {t("nav.order")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
